import { EventEmitter } from "node:events";
import type {
  LogEntry,
  LogLevel,
  LogQuery,
  LogQueryResult,
  LogSource,
} from "@skaile/workspaces/types";
import { ulid } from "ulid";
import { isStreamUsable, isUnwritableStreamError, markStreamBroken } from "./stream-health.js";
import type { LogSink } from "./sinks/types.js";

export interface LogStoreOptions {
  sessionId: string;
  level: LogLevel;
  sinks: LogSink[];
  flushIntervalMs?: number; // default 50
  flushBatchSize?: number; // default 100
}

export interface RecordInput {
  source: LogSource;
  level: LogLevel;
  message: string;
  data?: Record<string, unknown>;
  error?: { name: string; message: string; stack?: string };
}

const LEVEL_RANK: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

/**
 * In-memory log buffer with periodic flush to sinks.
 *
 * The LogStore buffers log entries and flushes them to registered sinks
 * either when the buffer reaches a certain size, after a time interval,
 * or when explicitly flushed. Supports subscribing to live log entries.
 *
 * @see {@link LogStoreOptions} for configuration
 * @see {@link LogEntry} for the log entry format
 * @category Logging
 * @since 2.0.0
 */
export class LogStore {
  private sessionId: string;
  private level: LogLevel;
  private sinks: LogSink[];
  private flushIntervalMs: number;
  private flushBatchSize: number;
  private buffer: LogEntry[] = [];
  private timer: NodeJS.Timeout | null = null;
  private emitter = new EventEmitter();
  private closed = false;

  constructor(opts: LogStoreOptions) {
    this.sessionId = opts.sessionId;
    this.level = opts.level;
    this.sinks = opts.sinks;
    this.flushIntervalMs = opts.flushIntervalMs ?? 50;
    this.flushBatchSize = opts.flushBatchSize ?? 100;
    this.emitter.setMaxListeners(0);
  }

  getLevel(): LogLevel {
    return this.level;
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  /** Append a sink after construction. Used by bridges (e.g. OnLogBridgeSink). */
  addSink(sink: LogSink): void {
    this.sinks.push(sink);
  }

  /** Returns true if the level is enabled. Used by createLogger to short-circuit. */
  isEnabled(level: LogLevel): boolean {
    return LEVEL_RANK[level] >= LEVEL_RANK[this.level];
  }

  record(input: RecordInput): void {
    if (this.closed) return;
    if (!this.isEnabled(input.level)) return;
    const entry: LogEntry = {
      id: ulid(),
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      source: input.source,
      level: input.level,
      message: input.message,
      data: input.data,
      error: input.error,
    };
    this.emitter.emit("entry", entry);
    this.buffer.push(entry);
    if (this.buffer.length >= this.flushBatchSize) {
      this.flushSync();
    } else if (this.flushIntervalMs > 0 && this.timer === null) {
      this.timer = setTimeout(() => this.flushSync(), this.flushIntervalMs);
    } else if (this.flushIntervalMs === 0) {
      this.flushSync();
    }
  }

  /** Subscribe to live entries. Returns an unsubscribe function. */
  subscribe(handler: (entry: LogEntry) => void): () => void {
    this.emitter.on("entry", handler);
    return () => this.emitter.off("entry", handler);
  }

  /**
   * Query stored log entries.
   *
   * Queries the first sink that supports querying (typically SQLiteSink).
   * Returns empty result if no sink supports querying.
   *
   * @param q - Query parameters including filters and pagination
   * @returns Query result with matching entries
   */
  query(q: LogQuery): LogQueryResult {
    for (const sink of this.sinks) {
      if (sink.query) return sink.query(q);
    }
    return { entries: [] };
  }

  /** Force-flush. SQLite writes are synchronous, so this returns once writes complete. */
  flush(): void {
    this.flushSync();
  }

  /**
   * Close the log store and release resources.
   *
   * Flushes any pending entries, marks the store as closed, and closes
   * all registered sinks.
   */
  close(): void {
    if (this.closed) return;
    this.flushSync();
    this.closed = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    for (const sink of this.sinks) sink.close();
    this.emitter.removeAllListeners();
  }

  private flushSync(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.buffer.length === 0) return;
    const batch = this.buffer;
    this.buffer = [];
    for (const sink of this.sinks) {
      try {
        sink.writeBatch(batch);
      } catch (err) {
        // Never let a sink failure crash the producer. `reportSinkFailure` is
        // itself failure-tolerant: a throw here would escape to the process
        // handler, which logs it back through this same store.
        reportSinkFailure(err);
      }
    }
  }
}

/**
 * Last-resort report of a sink failure. Silent once stderr is unusable —
 * a dead stderr fails asynchronously just like stdout, so the try/catch alone
 * would never latch. `isStreamUsable` is the shared verdict; the `catch` only
 * covers the synchronous throws (write-after-end, destroyed).
 */
function reportSinkFailure(err: unknown): void {
  if (!isStreamUsable(process.stderr)) return;
  try {
    process.stderr.write(`LogStore sink write failed: ${String(err)}\n`);
  } catch (writeErr) {
    if (isUnwritableStreamError(writeErr)) markStreamBroken(process.stderr);
  }
}
